/**
 * Seguimientos de la cuenta. Cada uno es un work item de Plane y su chip abre el
 * registro exacto — nunca el proyecto ni una búsqueda.
 *
 * El estado se muestra tal como lo escribieron en Plane (`En curso`,
 * `Bloqueado`); el panel solo usa `estadoGroup` para elegir el tono. Si mañana el
 * workspace renombra un estado, el chip lo dice bien sin tocar código.
 */
import type { Followup, Links } from '@ops/contracts'

import { formatFechaCorta } from '../../lib/format'
import { Button, RecordChip, Stack } from '../../ui'
import { Vacio } from '../comunes'

export interface SeguimientosClienteProps {
  followups: readonly Followup[]
  links: Links['followups']
  onCrear: () => void
}

export function SeguimientosCliente({
  followups,
  links,
  onCrear,
}: SeguimientosClienteProps) {
  if (followups.length === 0) {
    return (
      <Vacio
        punteada
        mensaje="Esta cuenta no tiene ningún seguimiento abierto."
        detalle="Todo lo que se asigne desde acá se crea como work item en Plane."
      >
        <RecordChip
          link={null}
          rotuloVacio="Sin seguimiento"
          onCrear={onCrear}
          textoCrear="Disparar seguimiento en Plane"
        />
      </Vacio>
    )
  }

  return (
    <Stack gap="sm">
      <ul className="flex list-none flex-col gap-2 p-0">
        {followups.map((followup) => (
          <li
            key={followup.id}
            data-testid="seguimiento"
            className="flex flex-wrap items-start justify-between gap-2 border-b border-border pb-2 last:border-b-0 last:pb-0"
          >
            <div className="min-w-0">
              <p className="text-meta text-text">{followup.titulo}</p>
              <p className="text-micro text-muted-strong">
                {followup.owner?.nombre ?? 'Sin responsable en Plane'}
                {followup.fechaObjetivo !== null
                  ? ` · para el ${formatFechaCorta(followup.fechaObjetivo) ?? followup.fechaObjetivo}`
                  : ' · sin fecha objetivo'}
              </p>
            </div>
            <RecordChip
              link={links[followup.id]?.plane ?? null}
              identificador={followup.planeIdentificador}
              estado={followup.estado}
              estadoGroup={followup.estadoGroup}
              rotuloVacio="No se pudo armar el link a Plane"
            />
          </li>
        ))}
      </ul>
      <div>
        <Button variante="secundario" icono="mas" onClick={onCrear}>
          Disparar otro seguimiento en Plane
        </Button>
      </div>
      <p className="text-micro text-muted-strong">
        El panel dispara los seguimientos; el estado siempre se lee de Plane.
      </p>
    </Stack>
  )
}
